/*!
 * NuveRise Quick View
 * --------------------------------------------------------------------
 * Opens a centered modal containing a product gallery + Add to Cart,
 * fed by /products/{handle}.js. Closes on backdrop / X / Esc. Focus is
 * trapped while open and restored to the triggering element on close.
 *
 * Public events dispatched:
 *   cart:add      — fires after a successful Add to Cart (cart.js will
 *                   pick it up via its own listeners).
 *   cart:refresh  — asks the global cart drawer to re-pull totals.
 *
 * No deps. ES5-safe-ish; uses async/await + arrow funcs (all modern
 * browsers — same baseline cart.js targets).
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-qv]');
  if (!root) return;

  const content = root.querySelector('[data-qv-content]');
  const modal = root.querySelector('.qv-modal');

  let selectedVariant = null;
  let currentProduct = null;
  let lastTrigger = null;
  let isOpen = false;

  /* ── OPEN / CLOSE ───────────────────────────────────────────────── */

  function open(trigger) {
    if (isOpen) return;
    isOpen = true;
    lastTrigger = trigger || document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Defer focus so the close button is actually painted
    setTimeout(function () {
      const closer = root.querySelector('[data-qv-close]:not([aria-hidden])') ||
                     root.querySelector('.qv-close');
      if (closer) closer.focus();
    }, 50);
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    content.innerHTML = '';
    selectedVariant = null;
    currentProduct = null;
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      try { lastTrigger.focus(); } catch (_) {}
    }
  }

  /* ── DATA FETCH ─────────────────────────────────────────────────── */

  async function load(handle, trigger) {
    open(trigger);
    content.innerHTML =
      '<div class="qv-loading">Loading…</div>';
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
      if (!res.ok) throw new Error('fetch_failed_' + res.status);
      const product = await res.json();
      currentProduct = product;
      selectedVariant =
        (product.variants || []).find(function (v) { return v.available; }) ||
        (product.variants || [])[0] ||
        null;
      if (!selectedVariant) {
        content.innerHTML =
          '<div class="qv-error">This product can\'t be added right now.</div>';
        return;
      }
      render(product);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[quick-view] load failed', err);
      content.innerHTML =
        '<div class="qv-error">Couldn\'t load this product. ' +
        '<a href="/products/' + escapeAttr(handle) + '" style="color:#10b981;text-decoration:underline;">View full page →</a></div>';
    }
  }

  /* ── RENDER ─────────────────────────────────────────────────────── */

  function render(p) {
    const images = (p.images && p.images.length)
      ? p.images
      : (p.featured_image ? [p.featured_image] : []);
    const variants = p.variants || [];
    const hasVariants = variants.length > 1;
    const desc = stripHtml(p.description || '').trim();
    const shortDesc = desc.length > 200 ? desc.slice(0, 200).trimEnd() + '…' : desc;
    const productUrl = p.url || ('/products/' + p.handle);

    content.innerHTML =
      '<div class="qv-gallery">' +
        (images.length
          ? '<img class="qv-gallery__main" id="qv-main-img" src="' + escapeAttr(images[0]) + '" alt="' + escapeAttr(p.title) + '">'
          : '<div class="qv-gallery__main" aria-hidden="true"></div>') +
        (images.length > 1
          ? '<div class="qv-thumbs" role="tablist" aria-label="Product images">' +
              images.slice(0, 5).map(function (img, i) {
                return '<button type="button" role="tab" data-qv-thumb="' + i + '" ' +
                       'aria-selected="' + (i === 0 ? 'true' : 'false') + '" ' +
                       'aria-label="Show image ' + (i + 1) + '">' +
                         '<img src="' + escapeAttr(img) + '" alt="" loading="lazy">' +
                       '</button>';
              }).join('') +
            '</div>'
          : '') +
      '</div>' +
      '<div class="qv-info">' +
        '<p class="qv-vendor">' + escapeHtml(p.vendor || 'NuveRise') + '</p>' +
        '<h2 id="qv-title">' + escapeHtml(p.title) + '</h2>' +
        '<p class="qv-price" data-qv-price>' + fmt(selectedVariant.price) + '</p>' +
        (shortDesc ? '<p class="qv-desc">' + escapeHtml(shortDesc) + '</p>' : '') +
        (hasVariants
          ? '<p class="qv-variant-label">Choose option</p>' +
            '<div class="qv-variant-row" role="radiogroup" aria-label="Product options">' +
              variants.map(function (v) {
                return '<button type="button" role="radio" ' +
                       'data-qv-variant="' + v.id + '" ' +
                       'aria-selected="' + (v.id === selectedVariant.id ? 'true' : 'false') + '" ' +
                       'aria-checked="' + (v.id === selectedVariant.id ? 'true' : 'false') + '"' +
                       (!v.available ? ' disabled aria-disabled="true"' : '') + '>' +
                         escapeHtml(v.title) +
                       '</button>';
              }).join('') +
            '</div>'
          : '') +
        '<button type="button" class="qv-add" data-qv-add' +
          (!selectedVariant.available ? ' disabled' : '') + '>' +
          (selectedVariant.available ? 'Add to cart' : 'Sold out') +
        '</button>' +
        '<a href="' + escapeAttr(productUrl) + '" class="qv-view-link">View full details →</a>' +
      '</div>';

    bindThumbs(images);
    bindVariants(variants);
    bindAddToCart(p);
  }

  function bindThumbs(images) {
    const thumbs = content.querySelectorAll('[data-qv-thumb]');
    const mainImg = content.querySelector('#qv-main-img');
    if (!mainImg || !thumbs.length) return;
    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        activateThumb(btn);
      });
    });
    function activateThumb(btn) {
      thumbs.forEach(function (b) { b.setAttribute('aria-selected', 'false'); });
      btn.setAttribute('aria-selected', 'true');
      const i = parseInt(btn.getAttribute('data-qv-thumb'), 10);
      if (!isNaN(i) && images[i]) {
        mainImg.src = images[i];
      }
    }
    // Arrow key cycling inside the thumb strip
    content.querySelector('.qv-thumbs').addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      const list = Array.prototype.slice.call(thumbs);
      const currentIdx = list.findIndex(function (b) {
        return b.getAttribute('aria-selected') === 'true';
      });
      if (currentIdx === -1) return;
      const nextIdx = e.key === 'ArrowRight'
        ? (currentIdx + 1) % list.length
        : (currentIdx - 1 + list.length) % list.length;
      e.preventDefault();
      list[nextIdx].focus();
      activateThumb(list[nextIdx]);
    });
  }

  function bindVariants(variants) {
    const btns = content.querySelectorAll('[data-qv-variant]');
    const priceEl = content.querySelector('[data-qv-price]');
    const addBtn = content.querySelector('[data-qv-add]');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const id = parseInt(btn.getAttribute('data-qv-variant'), 10);
        const v = variants.find(function (x) { return x.id === id; });
        if (!v) return;
        selectedVariant = v;
        btns.forEach(function (b) {
          b.setAttribute('aria-selected', 'false');
          b.setAttribute('aria-checked', 'false');
        });
        btn.setAttribute('aria-selected', 'true');
        btn.setAttribute('aria-checked', 'true');
        if (priceEl) priceEl.textContent = fmt(v.price);
        if (addBtn) {
          addBtn.disabled = !v.available;
          addBtn.textContent = v.available ? 'Add to cart' : 'Sold out';
        }
      });
    });
  }

  function bindAddToCart(p) {
    const addBtn = content.querySelector('[data-qv-add]');
    if (!addBtn) return;
    addBtn.addEventListener('click', async function () {
      if (!selectedVariant || !selectedVariant.available || addBtn.disabled) return;
      const originalLabel = addBtn.textContent;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ id: selectedVariant.id, quantity: 1 })
        });
        if (!res.ok) throw new Error('add_failed_' + res.status);

        document.dispatchEvent(new CustomEvent('cart:add', {
          detail: {
            product_id: p.id,
            variant_id: selectedVariant.id,
            quantity: 1,
            title: p.title,
            price: selectedVariant.price,
            total_value: selectedVariant.price
          }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        if (window.NuveCart && typeof window.NuveCart.open === 'function') {
          window.NuveCart.open();
        }

        close();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[quick-view] add failed', err);
        addBtn.textContent = 'Try again';
        addBtn.disabled = false;
        setTimeout(function () {
          if (addBtn && !addBtn.disabled) addBtn.textContent = originalLabel;
        }, 2200);
      }
    });
  }

  /* ── HELPERS ────────────────────────────────────────────────────── */

  function escapeHtml(s) {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }
  function escapeAttr(s) {
    return escapeHtml(s).replace(/"/g, '&quot;');
  }
  function stripHtml(s) {
    const d = document.createElement('div');
    d.innerHTML = s == null ? '' : String(s);
    return d.textContent || '';
  }
  function fmt(cents) {
    const n = Number(cents) / 100;
    if (!isFinite(n)) return '';
    return '$' + n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /* ── FOCUS TRAP ─────────────────────────────────────────────────── */

  function getFocusable() {
    return Array.prototype.slice.call(modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), ' +
      'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapTab(e) {
    if (e.key !== 'Tab') return;
    const focusable = getFocusable();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ── GLOBAL WIRING ──────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-quick-view]');
    if (trigger) {
      e.preventDefault();
      e.stopPropagation();
      const handle = trigger.getAttribute('data-handle');
      if (handle) load(handle, trigger);
      return;
    }
    if (e.target.closest('[data-qv-close]')) {
      e.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') trapTab(e);
  });
})();
